import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useEnvironment } from '../context/EnvironmentContext';
import { useOperations } from '../context/OperationsContext';
import SideMenu from '../components/SideMenu';
import './home.css';
import ProviderDrawer from '../components/ProviderDrawer';
import OperationalMap from '../components/OperationalMap';
import AsyncState from '../components/AsyncState';

const API_BASE = process.env.REACT_APP_CONSOLE_API_URL
  || (typeof window !== 'undefined' && window.location.hostname !== 'localhost' ? window.location.origin : 'http://localhost:8097');

function Icon({ name, size = 18 }) {
  const paths = {
    box: <><path d="M3 7.5 12 3l9 4.5v9L12 21l-9-4.5v-9Z" /><path d="m3 7.5 9 4.5 9-4.5M12 12v9" /></>,
    check: <path d="m5 12 4.2 4.2L19 6.5" />,
    pulse: <><path d="M3 12h3.5l2-5 4 10 2.2-5H21" /><path d="M12 3a9 9 0 1 1-6.36 2.64" /></>,
    arrow: <path d="M5 12h14m-6-6 6 6-6 6" />,
    refresh: <><path d="M20 11a8.2 8.2 0 0 0-14.7-4.8L3 9" /><path d="M3 4v5h5M4 13a8.2 8.2 0 0 0 14.7 4.8L21 15" /><path d="M21 20v-5h-5" /></>,
  };
  return <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name]}</svg>;
}

function useCountUp(value) {
  const [display, setDisplay] = useState(value);
  const previous = useRef(value);
  useEffect(() => {
    if (!Number.isFinite(value)) { setDisplay('—'); return; }
    const from = Number.isFinite(previous.current) ? previous.current : 0;
    const start = performance.now();
    const duration = 520;
    let frame;
    const update = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - ((1 - progress) ** 3);
      setDisplay(Math.round(from + ((value - from) * eased)));
      if (progress < 1) frame = requestAnimationFrame(update);
      else previous.current = value;
    };
    frame = requestAnimationFrame(update);
    return () => cancelAnimationFrame(frame);
  }, [value]);
  return display;
}

function MetricCard({ icon, label, value, suffix = '', detail, delta, onClick }) {
  const count = useCountUp(value);
  return (
    <button type="button" className="metric-card" onClick={onClick}>
      <span className="metric-icon"><Icon name={icon} /></span>
      <span className="metric-label">{label}</span>
      <strong className="metric-value">{count}{suffix}</strong>
      <span className="metric-detail">{detail}{delta != null && <b className={delta < 0 ? 'negative' : ''}>{delta > 0 ? '+' : ''}{delta}</b>}<Icon name="arrow" size={14} /></span>
    </button>
  );
}

function Reveal({ children, className = '' }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      setVisible(true);
      observer.disconnect();
    }, { threshold: 0.12 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return <div ref={ref} className={`reveal ${visible ? 'is-visible' : ''} ${className}`}>{children}</div>;
}

function Home() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { environment } = useEnvironment();
  const navigate = useNavigate();
  const { dashboard, error, updatedAt, loading: refreshing, refresh: loadDashboard, carrierReports, period, setPeriod } = useOperations();
  const [selectedService, setSelectedService] = useState(null);
  const [allServicesVisible, setAllServicesVisible] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [mapOpen, setMapOpen] = useState(false);

  const services = Array.isArray(dashboard?.services) ? dashboard.services : [];
  const visibleServices = allServicesVisible ? services : services.slice(0, 5);
  const healthyServices = services.filter((service) => service.status === 'healthy' || service.status === 'ok').length;
  const displayName = user?.name || user?.preferred_username || 'there';
  const delivered = Number.parseFloat(dashboard?.delivered_percent);
  const providers = Array.isArray(dashboard?.providers) ? dashboard.providers : [];
  const reportsByProvider = new Map((carrierReports || []).map((report) => [report.provider_code, report]));

  return (
    <div className="console-shell">
      <SideMenu />
      <main className="main-dashboard">
        <header className="main-header">
          <div>
            <p className="eyebrow">{t('mainDashboardEyebrow', { environment })}</p>
            <h1>{t('mainDashboard')}</h1>
            <p className="main-subtitle">{t('mainDashboardSubtitle', { name: displayName })}</p>
          </div>
          <div className="main-actions"><div className="period-switch">{['24h', '7d', '30d'].map((value) => <button type="button" className={period === value ? 'active' : ''} onClick={() => setPeriod(value)} key={value}>{value}</button>)}</div><button type="button" className={`refresh-button ${refreshing ? 'is-refreshing' : ''}`} onClick={loadDashboard} disabled={refreshing}><Icon name="refresh" size={16} /> {t('refresh')}</button></div>
        </header>

        <AsyncState loading={refreshing && !dashboard} error={error} onRetry={loadDashboard} onDiagnostics={() => navigate('/observability')}>

        <Reveal className="metrics-grid" aria-label="Shipment metrics">
          <MetricCard icon="box" label={t('mainDashboardShipmentRequests')} value={Number(dashboard?.calculations_total)} detail={t('mainDashboardShipmentRequestsHint')} delta={dashboard?.calculations_delta} onClick={() => navigate('/calculations')} />
          <MetricCard icon="check" label={t('mainDashboardRegisteredShipments')} value={Number(dashboard?.shipments_total)} detail={t('mainDashboardRegisteredShipmentsHint')} delta={dashboard?.shipments_delta} onClick={() => navigate('/calculations')} />
          <MetricCard icon="pulse" label={t('mainDashboardDelivered')} value={delivered} suffix="%" detail={t('mainDashboardFailureRate', { value: dashboard?.failure_percent ?? '—' })} delta={dashboard?.delivered_percent_delta} onClick={() => navigate('/calculations')} />
        </Reveal>

        <Reveal className="provider-scorecards">
          {providers.map((provider) => {
            const report = reportsByProvider.get(provider.provider_code) || {};
            const syncedAt = provider.pickup_points_last_synced_at ? new Date(provider.pickup_points_last_synced_at) : null;
            const ageHours = syncedAt ? (Date.now() - syncedAt.getTime()) / 3600000 : Infinity;
            const freshness = ageHours <= 24 ? 'Live' : ageHours <= 168 ? `${Math.round(ageHours)}h old` : 'Stale';
            return <button type="button" className="provider-scorecard" key={provider.id || provider.provider_code} onClick={() => setSelectedProvider(provider)}>
              <header><span>{provider.provider_code}</span><i className={provider.status === 'connected' ? 'connected' : ''}>{provider.status}</i></header>
              <div><strong>{report.delivered_percent || '—'}</strong><span>delivered</span></div>
              <dl><div><dt>Failure</dt><dd>{report.failure_percent || '—'}</dd></div><div><dt>Avg cost</dt><dd>{report.avg_cost || '—'}</dd></div><div><dt>Mappings</dt><dd>{provider.mapping_count ?? '—'}</dd></div></dl>
              <footer><span className={freshness === 'Stale' ? 'stale' : ''}>{freshness}</span><small>{provider.pickup_points_count || 0} pickup points</small></footer>
            </button>;
          })}
        </Reveal>

        <Reveal className="operational-map-wrap">
          {!mapOpen ? <section className="operational-map-launcher">
            <div><p className="eyebrow">{t('operationalMapLabel')}</p><h2>{t('operationalMap')}</h2><p>{t('operationalMapOnDemand')}</p></div>
            <button type="button" className="refresh-button" onClick={() => setMapOpen(true)}>{t('operationalMapOpen')}</button>
          </section> : <OperationalMap providers={providers} environment={environment} onClose={() => setMapOpen(false)} />}
        </Reveal>

        <Reveal className="dashboard-grid">
          <article className="dashboard-card service-card">
            <div className="card-heading">
              <div>
                <p className="eyebrow">RUNTIME</p>
                <h2>Service health</h2>
              </div>
              <span className={`health-summary ${services.length && healthyServices === services.length ? 'healthy' : ''}`}>
                <span />{healthyServices}/{services.length || '—'} operational
              </span>
            </div>
            <div className="service-list">
              {services.length ? visibleServices.map((service) => {
                const active = service.status === 'healthy' || service.status === 'ok';
                return <div className="service-row" key={service.name}>
                  <span className={`status-dot ${active ? 'is-healthy' : 'is-unhealthy'}`} />
                  <span>{service.name}</span>
                  <span className="service-status">{active ? 'Operational' : service.status || 'Unavailable'}</span>
                </div>;
              }) : <div className="empty-state">Checking service health...</div>}
            </div>
            {services.length > 5 && <button type="button" className="service-expand" onClick={() => setAllServicesVisible((visible) => !visible)}>{allServicesVisible ? 'Show core services' : `Show ${services.length - 5} more services`}</button>}
            <div className="service-topology" aria-label="Service topology">
              <span className="topology-core">CORE</span>
              {services.slice(0, 7).map((service) => {
                const active = service.status === 'healthy' || service.status === 'ok';
                return (
                  <button
                    type="button"
                    className={`topology-node ${active ? 'is-healthy' : 'is-unhealthy'}`}
                    key={service.name}
                    onClick={() => setSelectedService(selectedService?.name === service.name ? null : service)}
                    aria-label={`Show ${service.name} status`}
                  />
                );
              })}
            </div>
            {selectedService && <div className="topology-detail"><strong>{selectedService.name}</strong><span>{selectedService.status === 'ok' || selectedService.status === 'healthy' ? 'Operational' : selectedService.status || 'Unavailable'}</span></div>}
            <button type="button" className="card-link" onClick={() => navigate('/observability')}>Open observability <Icon name="arrow" size={15} /></button>
          </article>

          <article className="dashboard-card network-card">
            <div className="card-heading">
              <div>
                <p className="eyebrow">NETWORK</p>
                <h2>Carrier readiness</h2>
              </div>
              <span className="environment-tag">{environment}</span>
            </div>
            <div className="readiness-number">{dashboard?.connected_companies ?? '—'}</div>
            <p className="readiness-label">connected carrier integrations</p>
            <div className="carrier-bars" aria-label="Carrier integration readiness">
              {(providers.length ? providers : Array(8).fill({ provider_code: 'Pending', mapping_count: 0 })).slice(0, 8).map((provider, index) => (
                <button
                  type="button"
                  key={`${provider.provider_code}-${index}`}
                  style={{ height: `${Math.max(22, Math.min(94, Number(provider.mapping_count || 0) + 22))}%` }}
                  title={`${provider.provider_code}: ${provider.mapping_count || 0} mappings`}
                >
                  <span><strong>{provider.provider_code}</strong>{provider.mapping_count || 0} mappings</span>
                </button>
              ))}
            </div>
            <div className="network-foot"><span>{dashboard?.pickup_points_total ?? '—'} pickup points synced</span><button type="button" onClick={() => navigate('/integrations')}>Manage integrations <Icon name="arrow" size={14} /></button></div>
          </article>
        </Reveal>

        <footer className="dashboard-footer">
          <span>{updatedAt ? `Updated ${updatedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'Loading live data...'}</span>
          <span>FiluetShip control plane</span>
        </footer>
        </AsyncState>
      </main>
      {selectedProvider && <ProviderDrawer provider={selectedProvider} period={period} onClose={() => setSelectedProvider(null)} />}
    </div>
  );
}

export default Home;
